import { notifyOnError } from '~/rage/decorators';
import { Subject } from '~/rage/utils';
import {
	CaseItemRarity,
	CaseType,
	CasesBundleClientConfig,
	CasesLootboxClientConfig,
	CasesTagConfig,
	ClientError,
	ClientErrorCode,
	IClientConfigs,
	InventoryItemConfig,
	ParseRewardValueTypeMap,
	RewardType,
	isClothesItem,
	numWord,
} from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { INotificationsService } from '../NotificationsService';
import { IVehicleService } from '../VehicleService';
import { ICaseFeedItem, ICasesService } from './casesServiceTypes';

type DonateInventoryItemClientConfig =
	IClientConfigs['donate']['inventoryItems'][number];

export class CasesService implements ICasesService {
	private readonly MAX_DAYS_TO_SHOW_COUNTDOWN = 30;
	private readonly CASE_FEED_MAX_ITEMS = 20;

	private casesUpdatedAt = 0;
	private lootboxes = new Subject<CasesLootboxClientConfig[]>([]);
	private bundles = new Subject<CasesBundleClientConfig[]>([]);
	private caseTags = new Subject<CasesTagConfig[]>([]);
	private donateItems = new Subject<DonateInventoryItemClientConfig[]>([]);
	private inventoryItems = new Subject<InventoryItemConfig[]>([]);
	private caseFeed = new Subject<ICaseFeedItem[]>([]);

	constructor(
		private readonly backend: BackendService,
		private readonly gameConfig: GameConfigService,
		private readonly notifications: INotificationsService,
		private readonly vehicles: IVehicleService,
	) {
		this.gameConfig.getConfigs().subscribe((config) => {
			if (!config) {
				return;
			}

			if (config.cases) {
				this.lootboxes.next(
					config.cases.lootboxes as CasesLootboxClientConfig[],
				);
				this.bundles.next(config.cases.bundles as CasesBundleClientConfig[]);
				this.caseTags.next(config.cases.tags as CasesTagConfig[]);
			}

			if (config.donate) {
				this.donateItems.next(
					(config.donate?.inventoryItems ??
						[]) as DonateInventoryItemClientConfig[],
				);
			}

			if (config.inventory) {
				this.inventoryItems.next(config.inventory.items);
			}

			const existingFeed = this.caseFeed.getValue();
			if (existingFeed.length > 0) {
				this.caseFeed.next(
					existingFeed.map((item) => ({
						...item,
						rarity: this.getCaseFeedItemRarity(item),
					})),
				);
			}
		});

		this.backend.addEventHandler(
			'cases:onLootboxOpen',
			(data: ICaseFeedItem) => {
				this.pushCaseFeedItem(data);
			},
		);
	}

	private getCaseFeedItemRarity(data: ICaseFeedItem): CaseItemRarity {
		if (data.rarity) {
			return data.rarity;
		}

		const lootbox = this.getLootboxById(data.lootboxId);
		return (
			lootbox.items?.find((x) => x.donateItemId === data.rewardId)?.rarity ??
			CaseItemRarity.COMMON
		);
	}

	private pushCaseFeedItem(data: ICaseFeedItem): void {
		const enriched: ICaseFeedItem = {
			...data,
			id: data.id ?? `${Date.now()}_${Math.random().toString(16).slice(2)}`,
			rarity: this.getCaseFeedItemRarity(data),
		};

		this.caseFeed.next(
			[enriched, ...this.caseFeed.getValue()].slice(
				0,
				this.CASE_FEED_MAX_ITEMS,
			),
		);
	}

	@notifyOnError
	public getLootboxes(): Subject<CasesLootboxClientConfig[]> {
		return this.lootboxes;
	}

	@notifyOnError
	public getBundles(): Subject<CasesBundleClientConfig[]> {
		return this.bundles;
	}

	@notifyOnError
	public getLootboxById(id: string): CasesLootboxClientConfig {
		const item = this.lootboxes.getValue().find((x) => x.id === id);
		if (!item) {
			throw new ClientError(ClientErrorCode.CasesLootboxNotFound);
		}
		return item;
	}

	@notifyOnError
	public getBundleById(id: string): CasesBundleClientConfig {
		const item = this.bundles.getValue().find((x) => x.id === id);
		if (!item) {
			throw new ClientError(ClientErrorCode.CasesLootboxNotFound);
		}
		return item;
	}

	@notifyOnError
	public async buyLootbox(
		lootboxId: string,
		count?: number | undefined,
	): Promise<void> {
		await this.backend.invoke('cases:buyLootbox', {
			lootboxId,
			count,
		});
		const item = this.getLootboxById(lootboxId);
		if (item) {
			this.notifications.showSuccess(
				`Вы успешно приобрели кейс "${item.name}"`,
			);
		}
	}

	@notifyOnError
	public async buyBundle(bundleId: string): Promise<void> {
		await this.backend.invoke('cases:buyBundle', {
			bundleId,
		});
		const item = this.getBundleById(bundleId);
		if (item) {
			this.notifications.showSuccess(
				`Вы успешно приобрели набор "${item.name}"`,
			);
		}
	}

	@notifyOnError
	public async openLootbox(id: string): Promise<string> {
		const response = await this.backend.invoke<string>('cases:openLootbox', {
			lootboxId: id,
		});

		return response;
	}

	public getCaseTypeById(id: string): CaseType {
		const lootbox = this.lootboxes.getValue().find((x) => x.id === id);
		if (lootbox) {
			return CaseType.CASE;
		}
		const bundle = this.bundles.getValue().find((x) => x.id === id);
		if (bundle) {
			return CaseType.SET;
		}
		throw new ClientError(ClientErrorCode.CasesLootboxNotFound);
	}

	public getCaseTags(): Subject<CasesTagConfig[]> {
		return this.caseTags;
	}

	public getCaseTagById(id: string): CasesTagConfig | null {
		return this.caseTags.getValue().find((x) => x.id === id) ?? null;
	}

	public getRewardById(id: string): {
		type: RewardType;
		value: ParseRewardValueTypeMap[RewardType] | null;
	} {
		const item = this.donateItems.getValue().find((x) => x.itemId === id);
		if (!item) {
			return { type: RewardType.ITEM, value: null };
		}
		return {
			type: item.rewardType,
			value: item.rewardValue as ParseRewardValueTypeMap[RewardType],
		};
	}

	public getRewardNameById(id: string): string {
		const { type, value } = this.getRewardById(id);

		if (type === RewardType.VEHICLE) {
			return (
				this.vehicles.getVehicleNameByModel(
					(value as ParseRewardValueTypeMap[RewardType.VEHICLE])?.model,
				) ?? ''
			);
		}
		if (type === RewardType.ITEM) {
			const inventoryItem = value as ParseRewardValueTypeMap[RewardType.ITEM];
			const itemId =
				typeof inventoryItem === 'object' && inventoryItem?.id
					? inventoryItem.id
					: id;
			const inventoryItemConfig = this.inventoryItems
				.getValue()
				.find((x) => x.id === itemId);
			return inventoryItemConfig?.name ?? '';
		}
		if (type === RewardType.EXP) {
			const amount = (value as ParseRewardValueTypeMap[RewardType.EXP]) ?? 0;
			return `${amount.toLocaleString('ru-RU')} ед. опыта`;
		}
		if (type === RewardType.MONEY) {
			const amount = (value as ParseRewardValueTypeMap[RewardType.MONEY]) ?? 0;
			return `${amount.toLocaleString('ru-RU')} ${numWord(amount, [
				'рубль',
				'рубля',
				'рублей',
			])}`;
		}
		if (type === RewardType.LOOTBOX) {
			const lootbox = this.lootboxes.getValue().find((x) => x.id === value);
			if (lootbox) {
				return `Кейс "${lootbox.name}"`;
			}
			return 'Кейс';
		}
		if (type === RewardType.BUNDLE) {
			const bundle = this.bundles.getValue().find((x) => x.id === value);
			if (bundle) {
				return `Набор "${bundle.name}"`;
			}
			return 'Набор';
		}

		return '';
	}

	public getRewardImageId(id: string): string | null {
		const { type, value } = this.getRewardById(id);

		if (!value) {
			return id;
		}

		if (type === RewardType.VEHICLE) {
			const vehicleValue = value as ParseRewardValueTypeMap[RewardType.VEHICLE];
			return vehicleValue?.model ?? id;
		}

		if (type === RewardType.ITEM) {
			const inventoryItemValue =
				value as ParseRewardValueTypeMap[RewardType.ITEM];
			return inventoryItemValue?.id ?? id;
		}

		if (type === RewardType.LOOTBOX) {
			const lootboxId = value as ParseRewardValueTypeMap[RewardType.LOOTBOX];

			const lootboxById = this.lootboxes
				.getValue()
				.find((x) => x.id === lootboxId);
			if (lootboxById?.id) {
				return lootboxById.id;
			}

			const lootboxByDonateId = this.lootboxes
				.getValue()
				.find((x) => x.donateItemId === lootboxId);
			return lootboxByDonateId?.id ?? lootboxId;
		}

		if (type === RewardType.BUNDLE) {
			const bundleId = value as ParseRewardValueTypeMap[RewardType.BUNDLE];
			return bundleId;
		}

		return id;
	}

	@notifyOnError
	public getGroupedLootboxItems(lootboxId: string): Array<{
		type: RewardType;
		ids: string[];
		rarity?: CaseItemRarity;
	}> {
		const lootbox = this.getLootboxById(lootboxId);
		if (!lootbox.items) {
			return [];
		}

		const clothesConfig = this.gameConfig.configs?.clothes?.items ?? [];
		const result: Array<{
			type: RewardType;
			ids: string[];
			rarity?: CaseItemRarity;
		}> = [];

		const clothesGroups = new Map<
			string,
			{ ids: string[]; rarity?: CaseItemRarity }
		>();

		for (const item of lootbox.items) {
			const reward = this.getRewardById(item.donateItemId);

			if (reward.type === RewardType.ITEM && isClothesItem(item.donateItemId)) {
				const clothesItem = clothesConfig.find(
					(c) => c.id === item.donateItemId,
				);

				if (clothesItem) {
					const groupKey =
						(clothesItem as any).relDrawableId ?? clothesItem.drawableId;
					const key = `${clothesItem.componentId}_${groupKey}`;

					if (!clothesGroups.has(key)) {
						clothesGroups.set(key, { ids: [], rarity: item.rarity });
					}
					clothesGroups.get(key)!.ids.push(item.donateItemId);
				} else {
					result.push({
						type: RewardType.ITEM,
						ids: [item.donateItemId],
						rarity: item.rarity,
					});
				}
			} else {
				result.push({
					type: reward.type,
					ids: [item.donateItemId],
					rarity: item.rarity,
				});
			}
		}

		clothesGroups.forEach((group) => {
			result.push({
				type: RewardType.ITEM,
				ids: group.ids,
				rarity: group.rarity,
			});
		});

		const rarityOrder: Record<CaseItemRarity, number> = {
			[CaseItemRarity.LEGENDARY]: 0,
			[CaseItemRarity.EPIC]: 1,
			[CaseItemRarity.RARE]: 2,
			[CaseItemRarity.UNCOMMON]: 3,
			[CaseItemRarity.COMMON]: 4,
		};

		result.sort((a, b) => {
			const rarityA = rarityOrder[a.rarity || CaseItemRarity.COMMON];
			const rarityB = rarityOrder[b.rarity || CaseItemRarity.COMMON];
			return rarityA - rarityB;
		});

		return result;
	}

	getCaseFeed(): Subject<ICaseFeedItem[]> {
		return this.caseFeed;
	}
}
